import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Collapse,
  Popover,
  Button,
  Tooltip
} from "@mui/material";
import AlarmIcon from "@mui/icons-material/AlarmAddOutlined";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import { styled } from "@mui/material/styles";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import SendIcon from "@mui/icons-material/Send";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { format, parseISO, isToday, isTomorrow, addMinutes, setHours, setMinutes, addDays, startOfMinute } from "date-fns";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const CommentContainer = styled(Box)(({ theme }) => ({
  marginTop: 4,
  padding: "4px 8px",
  borderLeft: "3px solid #ffc107",
  backgroundColor: "rgba(255, 193, 7, 0.08)",
  borderRadius: "0 6px 6px 0",
}));

// Deixa clicavel a URL que vier dentro do comentario — a esteira de proposta manda
// o link do rascunho por aqui. Sem dependencia nova: quebra o texto pela URL e troca
// o pedaco por um <a>. O teste por parte usa regex sem /g (o /g guarda lastIndex e
// alternaria true/false entre chamadas).
const URL_NO_TEXTO = /(https?:\/\/[^\s<]+)/g;
const comLinks = texto =>
  String(texto || "").split(URL_NO_TEXTO).map((parte, i) =>
    /^https?:\/\//.test(parte) ? (
      <a
        key={i}
        href={parte}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#0f6c3f", textDecoration: "underline", wordBreak: "break-all" }}
      >
        {parte}
      </a>
    ) : (
      parte
    )
  );

const CommentText = styled(Typography)({
  fontSize: "0.75rem",
  lineHeight: 1.3,
  color: "#5f6368",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
});

const CommentMeta = styled(Typography)({
  fontSize: "0.65rem",
  color: "#9e9e9e",
  display: "flex",
  alignItems: "center",
  gap: 4,
});

// "hoje 18:00", "amanhã 09:00" ou "05/11 14:30"
const quando = (d) => {
  const x = typeof d === "string" ? parseISO(d) : d;
  if (isToday(x)) return `hoje ${format(x, "HH:mm")}`;
  if (isTomorrow(x)) return `amanhã ${format(x, "HH:mm")}`;
  return format(x, "dd/MM HH:mm");
};

const paraCampo = (d) => format(d, "yyyy-MM-dd'T'HH:mm");

// Lembrete pessoal (ideia do Jds 04/11): na hora marcada, quem comentou é
// avisado (som + aviso) e a conversa mostra o cartão do lembrete. Não vai
// nada para o cliente.
const MarcarLembrete = ({ comment }) => {
  const [el, setEl] = useState(null);
  const [valor, setValor] = useState("");
  const agora = new Date();
  const atalhos = [
    { rotulo: "Em 30 minutos", d: addMinutes(agora, 30) },
    { rotulo: "Em 1 hora", d: addMinutes(agora, 60) },
    ...(agora.getHours() < 18 ? [{ rotulo: "Hoje às 18:00", d: setMinutes(setHours(agora, 18), 0) }] : []),
    { rotulo: "Amanhã às 09:00", d: setMinutes(setHours(addDays(agora, 1), 9), 0) }
  ];
  const marcar = async (d) => {
    try {
      await api.put(`/message-comments/${comment.id}/lembrete`, { remindAt: d ? startOfMinute(d).toISOString() : null });
      setEl(null);
    } catch (err) {
      toastError(err);
    }
  };
  const pendente = comment.remindAt && !comment.remindedAt;
  return (
    <>
      <Tooltip title={pendente ? `Lembrete ${quando(comment.remindAt)}` : "Lembrar-me deste assunto"}>
        <IconButton
          size="small"
          aria-label="Lembrete"
          onClick={(e) => { setValor(paraCampo(comment.remindAt && pendente ? parseISO(comment.remindAt) : addMinutes(new Date(), 60))); setEl(e.currentTarget); }}
          sx={{ p: 0.2 }}
        >
          <AlarmIcon sx={{ fontSize: 12, color: pendente ? "#f57c00" : "#9e9e9e" }} />
        </IconButton>
      </Tooltip>
      <Popover open={!!el} anchorEl={el} onClose={() => setEl(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.75, width: 240 }}>
          <Typography variant="subtitle2">Lembrar-me</Typography>
          <Typography variant="caption" color="text.secondary">Só você é avisado. Nada vai para o cliente.</Typography>
          {atalhos.map((a) => (
            <Button key={a.rotulo} size="small" variant="outlined" onClick={() => marcar(a.d)} sx={{ justifyContent: "space-between", textTransform: "none" }}>
              {a.rotulo}<span style={{ opacity: 0.6, fontSize: "0.75rem" }}>{format(a.d, "HH:mm")}</span>
            </Button>
          ))}
          <TextField size="small" type="datetime-local" label="Outro dia e hora" value={valor} onChange={(e) => setValor(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mt: 0.5 }} />
          <Button size="small" variant="contained" disabled={!valor} onClick={() => marcar(new Date(valor))}>Marcar</Button>
          {pendente && <Button size="small" color="inherit" onClick={() => marcar(null)}>Tirar o lembrete</Button>}
        </Box>
      </Popover>
    </>
  );
};

const MessageComments = ({ comments = [], messageId, showInput, onCloseInput }) => {
  const { user } = useContext(AuthContext);
  const [newComment, setNewComment] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!newComment.trim()) return;
    try {
      setSaving(true);
      await api.post(`/message-comments/${messageId}`, { body: newComment.trim() });
      setNewComment("");
      if (onCloseInput) onCloseInput();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editingText.trim()) return;
    try {
      await api.put(`/message-comments/${id}`, { body: editingText.trim() });
      setEditingId(null);
      setEditingText("");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/message-comments/${id}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleKeyDown = (e, action) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      action();
    }
    if (e.key === "Escape") {
      if (editingId) {
        setEditingId(null);
      } else if (onCloseInput) {
        onCloseInput();
      }
    }
  };

  return (
    <Box sx={{ mt: 0.5 }}>
      {comments.map((comment) => (
        <CommentContainer key={comment.id}>
          {editingId === comment.id ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TextField
                size="small"
                fullWidth
                multiline
                maxRows={3}
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, () => handleUpdate(comment.id))}
                autoFocus
                variant="standard"
                sx={{ "& .MuiInput-input": { fontSize: "0.75rem", py: 0 } }}
              />
              <IconButton size="small" onClick={() => handleUpdate(comment.id)} sx={{ p: 0.3 }}>
                <CheckIcon sx={{ fontSize: 14, color: "#4caf50" }} />
              </IconButton>
              <IconButton size="small" onClick={() => setEditingId(null)} sx={{ p: 0.3 }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <CommentText>{comLinks(comment.body)}</CommentText>
                {user?.id === comment.userId && (
                  <Box sx={{ display: "flex", ml: 0.5, flexShrink: 0 }}>
                    <MarcarLembrete comment={comment} />
                    <IconButton
                      size="small"
                      onClick={() => { setEditingId(comment.id); setEditingText(comment.body); }}
                      sx={{ p: 0.2 }}
                    >
                      <EditIcon sx={{ fontSize: 12, color: "#9e9e9e" }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(comment.id)} sx={{ p: 0.2 }}>
                      <DeleteOutlineIcon sx={{ fontSize: 12, color: "#9e9e9e" }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
              <CommentMeta>
                <StickyNote2OutlinedIcon sx={{ fontSize: 10 }} />
                {comment.user?.name} - {comment.createdAt && format(parseISO(comment.createdAt), "dd/MM HH:mm")}
                {comment.remindAt && !comment.remindedAt && (
                  <span style={{ color: "#f57c00", display: "inline-flex", alignItems: "center", gap: 2, marginLeft: 4 }}>
                    <AlarmIcon sx={{ fontSize: 10 }} /> lembrete {quando(comment.remindAt)}
                  </span>
                )}
                {comment.remindedAt && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 2, marginLeft: 4 }}>
                    <AlarmOnIcon sx={{ fontSize: 10 }} /> lembrado {quando(comment.remindedAt)}
                  </span>
                )}
              </CommentMeta>
            </>
          )}
        </CommentContainer>
      ))}

      <Collapse in={showInput}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, px: 0.5 }}>
          <StickyNote2OutlinedIcon sx={{ fontSize: 14, color: "#ffc107" }} />
          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={3}
            placeholder="Comentário interno..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, handleSave)}
            autoFocus
            variant="standard"
            sx={{ "& .MuiInput-input": { fontSize: "0.75rem", py: 0 } }}
          />
          <IconButton size="small" onClick={handleSave} disabled={saving || !newComment.trim()} sx={{ p: 0.3 }}>
            <SendIcon sx={{ fontSize: 14, color: newComment.trim() ? "#ffc107" : "#ccc" }} />
          </IconButton>
          <IconButton size="small" onClick={onCloseInput} sx={{ p: 0.3 }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Collapse>
    </Box>
  );
};

export default MessageComments;
